import React, { useEffect, useState } from 'react';
import {
  Box,
  Image,
  chakra,
  Text,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  AspectRatio,
} from '@chakra-ui/react';
import { BenefitsType } from '../../services/bikletaPoints/dto';
import { typeOptions } from '../../utils/constants';
import { colors } from '../../utils/colors';
import { EmptyStateBenefit } from '../../assets';

export interface BenefitsCardPreviewProps {
  imageURL?: string | any;
  formikContext: any;
  benefitObject: Partial<BenefitsType> & {
    coverFile?: File | null;
    bannerFile?: File | null;
    listFile?: File | null;
  };
}

// Genera una object URL para un File y la limpia al desmontar/cambiar.
const useFilePreview = (file?: File | null): string | null => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setPreviewUrl(null);
    return undefined;
  }, [file]);

  return previewUrl;
};

const EmptyImage: React.FC = () => (
  <Stack
    direction={'column'}
    spacing={4}
    display={'flex'}
    justifyContent={'center'}
    alignItems={'center'}>
    <Box>
      <Image
        borderRadius={'16px'}
        objectFit={'none'}
        src={EmptyStateBenefit}
        alt="Article"
      />
    </Box>
    <Box w={'70%'}>
      <Text
        color={'#D8D7E2'}
        fontSize={16}
        fontWeight={'800'}
        lineHeight={'20px'}
        textAlign={'center'}>
        Todavía no cargaste ninguna foto
      </Text>
    </Box>
  </Stack>
);

const BenefitPreviewer: React.FC<BenefitsCardPreviewProps> = ({
  formikContext,
  benefitObject,
}) => {
  const {
    description,
    endDate,
    title,
    type,
    startDate,
    coverUrl,
    coverFile,
    bannerUrl,
    bannerFile,
    listUrl,
    listFile,
  } = benefitObject;

  const coverPreview = useFilePreview(coverFile);
  const bannerPreview = useFilePreview(bannerFile);
  const listPreview = useFilePreview(listFile);

  const detailImageUrl = coverPreview || coverUrl;
  const homeImageUrl = bannerPreview || bannerUrl;
  const listImageUrl = listPreview || listUrl;

  const typeParsed =
    typeOptions.find(option => option.value === type)?.label ??
    'Tipo de beneficio';

  const dates =
    endDate?.toString().length > 2 && startDate?.toString().length > 2
      ? `Desde ${startDate} hasta ${endDate}`
      : 'Desde 1999/01/01 Hasta 2000/01/01';

  const displayTitle = title?.length > 1 ? title : 'Título de tu beneficio';
  const displayTag = benefitObject?.benefitTag ?? 'Etiqueta';

  return (
    <Tabs variant={'soft-rounded'} colorScheme={'yellow'} size={'sm'}>
      <TabList flexWrap={'wrap'} gap={2}>
        {['Home', 'Lista', 'Detalle'].map(label => (
          <Tab
            key={label}
            color={colors.gray[0]}
            fontWeight={'600'}
            opacity={0.7}
            _hover={{ opacity: 1 }}
            _selected={{
              color: colors.purple[750],
              bg: colors.accent.main,
              opacity: 1,
            }}>
            {label}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {/* Home – Beneficios (560x350) */}
        <TabPanel px={0}>
          <Box
            mx="auto"
            w={'320px'}
            maxW={'100%'}
            borderRadius={'16px'}
            overflow={'hidden'}
            shadow="md"
            bg="white"
            _dark={{ bg: colors.purple[725] }}>
            <Box pos={'relative'} bgColor={'#86849A'}>
              <AspectRatio ratio={560 / 350}>
                {homeImageUrl ? (
                  <Image objectFit={'cover'} src={homeImageUrl} alt="Home" />
                ) : (
                  <EmptyImage />
                )}
              </AspectRatio>
              <Box
                pos={'absolute'}
                top={3}
                right={0}
                px={3}
                py={1}
                bgColor={
                  benefitObject?.benefitTag ? colors.accent.main : '#D8D7E2'
                }
                borderTopLeftRadius={'full'}
                borderBottomLeftRadius={'full'}>
                <Text textColor={'black'} fontSize={'small'}>
                  {displayTag}
                </Text>
              </Box>
            </Box>
            <Box p={3}>
              <Text
                noOfLines={1}
                color="#FDFDFC"
                fontWeight="700"
                fontSize={16}
                lineHeight={'20px'}>
                {displayTitle}
              </Text>
              <Text
                fontSize={'13px'}
                fontWeight={'400'}
                lineHeight={'18px'}
                color="#FDFDFC">
                {typeParsed}
              </Text>
            </Box>
          </Box>
        </TabPanel>

        {/* Lista Beneficios (1200x600) */}
        <TabPanel px={0}>
          <Box
            mx="auto"
            w={'340px'}
            maxW={'100%'}
            borderRadius={'16px'}
            overflow={'hidden'}
            shadow="md"
            bg="white"
            _dark={{ bg: colors.purple[725] }}>
            <Box pos={'relative'} bgColor={'#86849A'}>
              <AspectRatio ratio={1200 / 600}>
                {listImageUrl ? (
                  <Image objectFit={'cover'} src={listImageUrl} alt="Lista" />
                ) : (
                  <EmptyImage />
                )}
              </AspectRatio>
              <Box
                pos={'absolute'}
                top={3}
                right={0}
                px={3}
                py={1}
                bgColor={
                  benefitObject?.benefitTag ? colors.accent.main : '#D8D7E2'
                }
                borderTopLeftRadius={'full'}
                borderBottomLeftRadius={'full'}>
                <Text textColor={'black'} fontSize={'small'}>
                  {displayTag}
                </Text>
              </Box>
            </Box>
            <Box p={3}>
              <Text
                noOfLines={1}
                color="#FDFDFC"
                fontWeight="700"
                fontSize={16}
                lineHeight={'20px'}>
                {displayTitle}
              </Text>
              <Text
                fontSize={'13px'}
                fontWeight={'400'}
                lineHeight={'18px'}
                color="#FDFDFC">
                {typeParsed}
              </Text>
            </Box>
          </Box>
        </TabPanel>

        {/* Detalle Beneficio (1320x1100) */}
        <TabPanel px={0}>
          <Box overflow={'hidden'} borderRadius="16px">
            <Box
              h={'590px'}
              mx="auto"
              w={'366px'}
              borderRadius={'16px'}
              pos="relative"
              shadow="md"
              bg="white"
              _dark={{ bg: colors.purple[725] }}
              maxW="md">
              <Box
                h={'50%'}
                display={'flex'}
                justifyContent={'center'}
                bgColor={'#86849A'}
                borderRadius={'16px'}>
                {detailImageUrl ? (
                  <Image
                    borderRadius={'16px'}
                    objectFit={'cover'}
                    src={detailImageUrl}
                    alt="Detalle"
                  />
                ) : (
                  <EmptyImage />
                )}
              </Box>

              <Box
                pos={'absolute'}
                top={5}
                right={0}
                px={3}
                py={1}
                bgColor={
                  benefitObject?.benefitTag ? colors.accent.main : '#D8D7E2'
                }
                borderTopLeftRadius={'full'}
                borderBottomLeftRadius={'full'}>
                <Text textColor={'black'} fontSize={'small'}>
                  {displayTag}
                </Text>
              </Box>

              <Box p={4}>
                <Box px={2}>
                  <Text
                    display="block"
                    noOfLines={2}
                    color="#FDFDFC"
                    fontWeight="700"
                    fontSize={20}
                    lineHeight={'24px'}
                    mt={2}>
                    {displayTitle}
                  </Text>
                  <chakra.span
                    fontSize="16px"
                    fontWeight={'700'}
                    lineHeight={'20px'}
                    color="#FDFDFC"
                    _dark={{ color: '#FDFDFC' }}>
                    {typeParsed}
                  </chakra.span>
                  <chakra.p
                    fontSize={'15px'}
                    lineHeight={'20px'}
                    fontWeight={'400'}
                    color="#FDFDFC"
                    _dark={{ color: '#FDFDFC' }}>
                    {dates}
                  </chakra.p>
                  <chakra.p
                    mt={5}
                    noOfLines={5}
                    fontSize="15px"
                    fontWeight="400"
                    lineHeight={'20px'}
                    color="#FDFDFC"
                    _dark={{ color: '#FDFDFC' }}>
                    {description?.length > 1
                      ? description
                      : '¡Oferta imperdible! Ahorra un 10% en todas tus compras. Descubre una amplia selección de bicicletas de alta calidad y accesorios de primer nivel. Visítanos hoy y aprovecha este descuento exclusivo. ¡Pedalea hacia grandes ahorros!'}
                  </chakra.p>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default BenefitPreviewer;
